import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSeedling, faCheckCircle, faGlobeAmericas, faAward, 
  faLeaf, faMicroscope, faShieldAlt, faTractor, faChartLine,
  faCertificate, faHandshake, faStar, faArrowRight
} from '@fortawesome/free-solid-svg-icons';

const AboutUsStyles = () => {
  useEffect(() => {
    const css = `


      :root {
        --primary-blue: #0073b1;
        --primary-green: #6bbe44;
        --dark-blue: #004d73;
        --light-green: #5aa838;
        --text-dark: #0a1828;
        --text-medium: #2d3748;
        --text-light: #4a5568;
        --text-lighter: #718096;
        --bg-light: #fafbfc;
        --bg-white: #ffffff;
        --border-light: rgba(0, 115, 177, 0.08);
        --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.04);
        --shadow-md: 0 8px 24px rgba(0, 77, 115, 0.08);
        --shadow-lg: 0 20px 60px rgba(0, 77, 115, 0.15);
        --shadow-xl: 0 30px 80px rgba(0, 77, 115, 0.25);
        --smooth-ease: cubic-bezier(0.4, 0, 0.2, 1);
        --bounce-ease: cubic-bezier(0.68, -0.55, 0.265, 1.55);
        --spring-ease: cubic-bezier(0.175, 0.885, 0.32, 1.275);
      }
    
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(60px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes fadeInLeft {
        from {
          opacity: 0;
          transform: translateX(-40px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }

      @keyframes fadeInRight {
        from {
          opacity: 0;
          transform: translateX(40px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }

      @keyframes float {
        0%, 100% {
          transform: translateY(0px);
        }
        50% {
          transform: translateY(-15px);
        }
      }

      @keyframes pulse {
        0%, 100% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.05);
        }
      }

      @keyframes shimmer {
        0% {
          background-position: -1000px 0;
        }
        100% {
          background-position: 1000px 0;
        }
      }

      @keyframes slideInScale {
        from {
          opacity: 0;
          transform: translateY(30px) scale(0.95);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }

      @keyframes gradientShift {
        0% {
          background-position: 0% 50%;
        }
        50% {
          background-position: 100% 50%;
        }
        100% {
          background-position: 0% 50%;
        }
      }

      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      .about-us-page {
        padding: 0;
        margin: 0;
        background: var(--bg-white);
        overflow-x: hidden;
        font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif;
        color: var(--text-dark);
      }

      /* --- Hero Section --- */
      .about-hero {
        position: relative;
        min-height: 90vh;
        display: flex;
        align-items: center;
        background: linear-gradient(135deg, #0073b1 0%, #004d73 50%, #0a1828 100%);
        background-size: 200% 200%;
        animation: gradientShift 15s ease infinite;
        padding: 140px 40px 100px;
        overflow: hidden;
      }

      .about-hero::before {
        content: '';
        position: absolute;
        inset: 0;
        background-image: 
          radial-gradient(circle at 20% 50%, rgba(107, 190, 68, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(107, 190, 68, 0.08) 0%, transparent 50%);
        z-index: 0;
      }

      .about-hero::after {
        content: '';
        position: absolute;
        inset: 0;
        background-image: 
          linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
        background-size: 50px 50px;
        z-index: 0;
      }

      .about-hero-container {
        max-width: 1400px;
        margin: 0 auto;
        position: relative;
        z-index: 2;
        display: grid;
        grid-template-columns: 1.2fr 1fr;
        gap: 80px;
        align-items: center;
      }

      .about-hero-content {
        animation: fadeInLeft 1s var(--smooth-ease) 0.2s both;
      }

      .about-hero-label {
        display: inline-block;
        font-size: 0.85rem;
        font-weight: 700;
        letter-spacing: 2px;
        text-transform: uppercase;
        color: var(--primary-green);
        margin-bottom: 24px;
        padding: 8px 20px;
        background: rgba(107, 190, 68, 0.15);
        border-radius: 50px;
        border: 1px solid rgba(107, 190, 68, 0.3);
      }

      .about-hero-headline {
        font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif;
        font-size: clamp(3rem, 6vw, 5.5rem);
        color: white;
        line-height: 1.1;
        margin-bottom: 28px;
        font-weight: 700;
        letter-spacing: -1.5px;
      }

      .about-hero-headline .highlight {
        background: linear-gradient(135deg, #6bbe44 0%, #7ed957 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        font-family: inherit;
      }

      .about-hero-text {
        font-size: 1.25rem;
        line-height: 1.8;
        color: rgba(255, 255, 255, 0.9);
        margin-bottom: 32px;
        font-weight: 400;
        max-width: 600px;
      }

      .about-hero-quote {
        font-size: 1.15rem;
        font-style: italic;
        color: rgba(255, 255, 255, 0.85);
        padding: 28px 32px;
        background: rgba(255, 255, 255, 0.08);
        border-left: 4px solid var(--primary-green);
        border-radius: 0 12px 12px 0;
        backdrop-filter: blur(10px);
        max-width: 600px;
        position: relative;
        box-shadow: var(--shadow-md);
      }

      .about-hero-image-wrapper {
        position: relative;
        animation: fadeInRight 1s var(--smooth-ease) 0.4s both;
      }

      .about-hero-image-wrapper::before {
        content: '';
        position: absolute;
        inset: -15px;
        background: linear-gradient(135deg, rgba(107, 190, 68, 0.3), rgba(0, 115, 177, 0.3));
        border-radius: 24px;
        z-index: -1;
        filter: blur(30px);
        opacity: 0.6;
        animation: pulse 4s ease-in-out infinite;
      }

      .about-hero-image-wrapper img {
        width: 100%;
        height: auto;
        border-radius: 20px;
        box-shadow: var(--shadow-xl);
        object-fit: cover;
        max-height: 500px;
        transition: all 0.6s var(--smooth-ease);
      }
      
      .about-hero-image-wrapper:hover img {
        transform: scale(1.03) translateY(-8px);
        box-shadow: 0 40px 100px rgba(0, 0, 0, 0.3);
      }

      /* --- Stats Banner --- */
      .stats-banner {
        background: linear-gradient(135deg, var(--primary-green) 0%, var(--light-green) 100%);
        padding: 60px 40px;
        margin-top: -50px;
        position: relative;
        z-index: 3;
      }

      .stats-container {
        max-width: 1200px;
        margin: 0 auto;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 40px;
      }

      .stat-item {
        text-align: center;
        color: white;
        animation: fadeInUp 0.8s var(--smooth-ease) both;
      }

      .stat-item:nth-child(1) { animation-delay: 0.1s; }
      .stat-item:nth-child(2) { animation-delay: 0.2s; }
      .stat-item:nth-child(3) { animation-delay: 0.3s; }
      .stat-item:nth-child(4) { animation-delay: 0.4s; }

      .stat-number {
        font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif;
        font-size: 3.5rem;
        font-weight: 700;
        line-height: 1;
        margin-bottom: 8px;
        text-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      }

      .stat-label {
        font-size: 1rem;
        font-weight: 600;
        opacity: 0.95;
        text-transform: uppercase;
        letter-spacing: 1px;
      }

      /* --- Section Container --- */
      .section-container {
        max-width: 1400px;
        margin: 0 auto;
        padding: 120px 40px;
      }

      .section-container.bg-light {
        background: var(--bg-light);
      }

      /* --- Section Header --- */
      .section-header {
        text-align: center;
        margin-bottom: 80px;
        animation: fadeInUp 0.8s var(--smooth-ease) both;
      }

      .section-label {
        display: inline-block;
        font-size: 0.8rem;
        font-weight: 700;
        letter-spacing: 2px;
        text-transform: uppercase;
        color: var(--primary-green);
        margin-bottom: 16px;
        padding: 6px 18px;
        background: rgba(107, 190, 68, 0.1);
        border-radius: 50px;
      }

      .section-title {
        font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif;
        font-size: clamp(2.5rem, 5vw, 4.5rem);
        color: var(--text-dark);
        margin-bottom: 24px;
        font-weight: 700;
        letter-spacing: -1.5px;
        line-height: 1.15;
      }

      .section-subtitle {
        font-size: 1.25rem;
        color: var(--text-light);
        line-height: 1.7;
        max-width: 800px;
        margin: 0 auto;
      }

      /* --- Vision & Mission Cards --- */
      .vision-mission-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
        gap: 50px;
        margin-top: 20px;
      }

      .vm-card {
        background: white;
        border-radius: 28px;
        padding: 60px 50px;
        box-shadow: var(--shadow-md);
        border: 2px solid var(--border-light);
        transition: all 0.5s var(--smooth-ease);
        position: relative;
        overflow: hidden;
        animation: slideInScale 0.8s var(--smooth-ease) both;
      }

      .vm-card:nth-child(1) { animation-delay: 0.2s; }
      .vm-card:nth-child(2) { animation-delay: 0.4s; }

      .vm-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 8px;
        background: linear-gradient(90deg, var(--primary-green), var(--primary-blue));
        transform: scaleX(0);
        transition: transform 0.6s var(--smooth-ease);
        transform-origin: left;
      }

      .vm-card::after {
        content: '';
        position: absolute;
        inset: 0;
        background: radial-gradient(circle at top right, rgba(107, 190, 68, 0.05) 0%, transparent 70%);
        opacity: 0;
        transition: opacity 0.5s var(--smooth-ease);
      }

      .vm-card:hover::before {
        transform: scaleX(1);
      }

      .vm-card:hover::after {
        opacity: 1;
      }

      .vm-card:hover {
        transform: translateY(-12px);
        box-shadow: var(--shadow-xl);
        border-color: var(--primary-green);
      }

      .vm-card-header {
        display: flex;
        align-items: center;
        gap: 20px;
        margin-bottom: 32px;
      }

      .vm-icon {
        width: 80px;
        height: 80px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, var(--primary-green), var(--light-green));
        border-radius: 20px;
        box-shadow: 0 8px 24px rgba(107, 190, 68, 0.3);
        transition: all 0.4s var(--bounce-ease);
      }

      .vm-card:hover .vm-icon {
        transform: scale(1.1) rotate(5deg);
        box-shadow: 0 12px 32px rgba(107, 190, 68, 0.4);
      }

      .vm-icon svg {
        font-size: 2.5rem;
        color: white;
      }

      .vm-card h3 {
        font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif;
        font-size: 2.5rem;
        color: var(--text-dark);
        font-weight: 700;
        letter-spacing: -1px;
      }

      .vm-card-content p {
        font-size: 1.2rem;
        line-height: 1.9;
        color: var(--text-light);
        margin-bottom: 28px;
      }

      .mission-list {
        list-style: none;
        padding: 0;
        margin: 0;
      }

      .mission-list li {
        font-size: 1.15rem;
        line-height: 1.8;
        color: var(--text-medium);
        margin-bottom: 20px;
        padding-left: 40px;
        position: relative;
        transition: all 0.3s var(--smooth-ease);
      }

      .mission-list li::before {
        content: '';
        position: absolute;
        left: 0;
        top: 8px;
        width: 24px;
        height: 24px;
        background: linear-gradient(135deg, var(--primary-green), var(--light-green));
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(107, 190, 68, 0.3);
      }

      .mission-list li::after {
        content: '✓';
        position: absolute;
        left: 6px;
        top: 8px;
        color: white;
        font-weight: 700;
        font-size: 0.9rem;
      }

      .mission-list li:hover {
        transform: translateX(8px);
        color: var(--primary-blue);
      }

      /* --- Farm Section --- */
      .farm-section {
        display: grid;
        grid-template-columns: 1fr 1.2fr;
        gap: 80px;
        align-items: center;
        animation: fadeInUp 0.8s var(--smooth-ease) both;
      }

      .farm-image {
        position: relative;
        border-radius: 24px;
        overflow: hidden;
        box-shadow: var(--shadow-lg);
        transition: all 0.6s var(--smooth-ease);
      }

      .farm-image::before {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(135deg, rgba(107, 190, 68, 0.2) 0%, rgba(0, 115, 177, 0.2) 100%);
        opacity: 0;
        transition: opacity 0.5s var(--smooth-ease);
        z-index: 1;
      }

      .farm-image:hover::before {
        opacity: 1;
      }

      .farm-image:hover {
        transform: scale(1.02) translateY(-8px);
        box-shadow: var(--shadow-xl);
      }

      .farm-image img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        transition: all 0.6s var(--smooth-ease);
      }

      .farm-image:hover img {
        transform: scale(1.05);
      }

      .farm-content h3 {
        font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif;
        font-size: 3rem;
        color: var(--text-dark);
        margin-bottom: 32px;
        font-weight: 700;
        letter-spacing: -1px;
        display: flex;
        align-items: center;
        gap: 16px;
      }

      .farm-content h3 svg {
        color: var(--primary-green);
        font-size: 3rem;
      }

      .farm-content > p {
        font-size: 1.2rem;
        line-height: 1.9;
        color: var(--text-light);
        margin-bottom: 36px;
      }

      .farm-features {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 24px;
        margin-bottom: 40px;
      }

      .farm-feature-item {
        padding: 24px;
        background: linear-gradient(135deg, rgba(107, 190, 68, 0.05) 0%, rgba(0, 115, 177, 0.05) 100%);
        border-radius: 16px;
        border-left: 4px solid var(--primary-green);
        transition: all 0.4s var(--smooth-ease);
      }

      .farm-feature-item:hover {
        transform: translateX(8px);
        background: linear-gradient(135deg, rgba(107, 190, 68, 0.1) 0%, rgba(0, 115, 177, 0.1) 100%);
        box-shadow: var(--shadow-md);
      }

      .farm-feature-item p {
        font-size: 1.1rem;
        color: var(--text-medium);
        font-weight: 500;
        margin: 0;
      }

      .farm-highlight {
        padding: 32px;
        background: linear-gradient(135deg, var(--primary-blue) 0%, var(--dark-blue) 100%);
        border-radius: 20px;
        color: white;
        box-shadow: var(--shadow-lg);
      }

      .farm-highlight p {
        font-size: 1.25rem;
        line-height: 1.8;
        font-weight: 600;
        margin: 0;
      }

      /* --- Why Rafferty Grid --- */
      .why-rafferty-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
        gap: 32px;
        margin-top: 60px;
      }

      .why-item {
        background: white;
        border-radius: 24px;
        padding: 40px 32px;
        box-shadow: var(--shadow-md);
        border: 2px solid var(--border-light);
        transition: all 0.5s var(--spring-ease);
        position: relative;
        overflow: hidden;
        animation: slideInScale 0.8s var(--smooth-ease) both;
      }

      .why-item:nth-child(1) { animation-delay: 0.1s; }
      .why-item:nth-child(2) { animation-delay: 0.2s; }
      .why-item:nth-child(3) { animation-delay: 0.3s; }
      .why-item:nth-child(4) { animation-delay: 0.4s; }
      .why-item:nth-child(5) { animation-delay: 0.5s; }

      .why-item::before {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 100px;
        height: 100px;
        background: radial-gradient(circle, rgba(107, 190, 68, 0.1) 0%, transparent 70%);
        border-radius: 0 0 0 100%;
        transition: all 0.5s var(--smooth-ease);
      }

      .why-item:hover::before {
        width: 200px;
        height: 200px;
        background: radial-gradient(circle, rgba(107, 190, 68, 0.15) 0%, transparent 70%);
      }

      .why-item:hover {
        transform: translateY(-12px) scale(1.02);
        box-shadow: var(--shadow-xl);
        border-color: var(--primary-green);
      }

      .why-item-header {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 20px;
      }

      .why-number {
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, var(--primary-green), var(--light-green));
        color: white;
        border-radius: 14px;
        font-size: 1.5rem;
        font-weight: 700;
        font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif;
        box-shadow: 0 6px 20px rgba(107, 190, 68, 0.3);
        transition: all 0.4s var(--bounce-ease);
      }

      .why-item:hover .why-number {
        transform: scale(1.15) rotate(5deg);
        box-shadow: 0 8px 28px rgba(107, 190, 68, 0.4);
      }

      .why-item h4 {
        font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif;
        font-size: 1.6rem;
        color: var(--text-dark);
        font-weight: 600;
        letter-spacing: -0.5px;
        flex: 1;
      }

      .why-item p {
        font-size: 1.1rem;
        line-height: 1.8;
        color: var(--text-light);
        margin: 0;
      }

      /* --- Testing Section --- */
      .testing-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
        gap: 40px;
        margin-top: 60px;
      }

      .testing-card {
        background: white;
        border-radius: 28px;
        padding: 50px 40px;
        box-shadow: var(--shadow-md);
        border: 2px solid var(--border-light);
        text-align: center;
        transition: all 0.5s var(--spring-ease);
        position: relative;
        overflow: hidden;
        animation: slideInScale 0.8s var(--smooth-ease) both;
      }

      .testing-card:nth-child(1) { animation-delay: 0.2s; }
      .testing-card:nth-child(2) { animation-delay: 0.4s; }
      .testing-card:nth-child(3) { animation-delay: 0.6s; }

      .testing-card::before {
        content: '';
        position: absolute;
        inset: 0;
        background: radial-gradient(circle at center, rgba(107, 190, 68, 0.05) 0%, transparent 70%);
        opacity: 0;
        transition: opacity 0.5s var(--smooth-ease);
      }

      .testing-card:hover::before {
        opacity: 1;
      }

      .testing-card:hover {
        transform: translateY(-16px);
        box-shadow: var(--shadow-xl);
        border-color: var(--primary-green);
      }

      .testing-icon-wrapper {
        width: 100px;
        height: 100px;
        margin: 0 auto 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, var(--primary-green), var(--light-green));
        border-radius: 50%;
        box-shadow: 0 12px 32px rgba(107, 190, 68, 0.3);
        transition: all 0.5s var(--bounce-ease);
        position: relative;
      }

      .testing-icon-wrapper::before {
        content: '';
        position: absolute;
        inset: -8px;
        background: linear-gradient(135deg, var(--primary-green), var(--light-green));
        border-radius: 50%;
        opacity: 0.2;
        filter: blur(12px);
        transition: all 0.5s var(--smooth-ease);
      }

      .testing-card:hover .testing-icon-wrapper {
        transform: scale(1.1) rotate(8deg);
        box-shadow: 0 16px 40px rgba(107, 190, 68, 0.4);
      }

      .testing-card:hover .testing-icon-wrapper::before {
        inset: -16px;
        opacity: 0.3;
      }

      .testing-icon-wrapper svg {
        font-size: 3rem;
        color: white;
        position: relative;
        z-index: 1;
      }

      .testing-card h4 {
        font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif;
        font-size: 1.8rem;
        color: var(--text-dark);
        margin-bottom: 16px;
        font-weight: 600;
        letter-spacing: -0.5px;
      }

      .testing-card p {
        font-size: 1.15rem;
        line-height: 1.8;
        color: var(--text-light);
        margin: 0;
      }

      .testing-footer {
        margin-top: 60px;
        padding: 50px;
        background: linear-gradient(135deg, rgba(107, 190, 68, 0.1) 0%, rgba(0, 115, 177, 0.1) 100%);
        border-radius: 24px;
        border: 2px solid rgba(107, 190, 68, 0.2);
        text-align: center;
        position: relative;
        overflow: hidden;
      }

      .testing-footer::before {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(135deg, transparent 0%, rgba(107, 190, 68, 0.05) 50%, transparent 100%);
        animation: shimmer 3s linear infinite;
        background-size: 200% 100%;
      }

      .testing-footer-content {
        position: relative;
        z-index: 1;
      }

      .testing-footer-icon {
        font-size: 3rem;
        color: var(--primary-green);
        margin-bottom: 20px;
      }

      .testing-footer p {
        font-size: 1.35rem;
        color: var(--text-medium);
        font-weight: 700;
        margin: 0;
        line-height: 1.7;
      }

      /* --- Responsive Design --- */
      @media (max-width: 1200px) {
        .about-hero-container {
          grid-template-columns: 1fr;
          gap: 60px;
          text-align: center;
        }

        .about-hero-content {
          max-width: 100%;
        }

        .about-hero-text,
        .about-hero-quote {
          margin-left: auto;
          margin-right: auto;
        }

        .farm-section {
          grid-template-columns: 1fr;
        }

        .farm-image {
          order: -1;
        }
      }

      @media (max-width: 900px) {
        .vision-mission-grid {
          grid-template-columns: 1fr;
        }

        .why-rafferty-grid {
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        }

        .testing-grid {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 768px) {
        .section-container {
          padding: 80px 24px;
        }

        .about-hero {
          padding: 100px 24px 80px;
        }

        .about-hero-headline {
          font-size: 2.5rem;
        }

        .section-title {
          font-size: 2.2rem;
        }

        .vm-card {
          padding: 40px 32px;
        }

        .farm-content h3 {
          font-size: 2.2rem;
        }

        .testing-footer {
          padding: 40px 28px;
        }
      }

      @media (max-width: 480px) {
        .about-hero-headline {
          font-size: 2rem;
        }

        .section-title {
          font-size: 1.8rem;
        }

        .stats-container {
          grid-template-columns: 1fr;
        }

        .vm-card,
        .why-item,
        .testing-card {
          padding: 32px 24px;
        }
      }
    `;

    const styleEl = document.createElement('style');
    styleEl.id = 'about-us-styles';
    styleEl.textContent = css;
    document.head.appendChild(styleEl);

    return () => {
      const el = document.getElementById('about-us-styles');
      if (el) {
        document.head.removeChild(el);
      }
    };
  }, []);

  return null;
};

function AboutUs() {
  return (
    <>
      <AboutUsStyles />
      <div className="about-us-page">

        {/* --- Hero Section --- */}
        <section className="about-hero">
          <div className="about-hero-container">
            <div className="about-hero-content">
              <div className="about-hero-label">
                <FontAwesomeIcon icon={faStar} style={{marginRight: '8px'}} />
                Egyptian Excellence Since 2003
              </div>
              <h1 className="about-hero-headline">
                Our Roots, <span className="highlight">Our Promise</span>
              </h1>
              <p className="about-hero-text">
                Welcome to Rafferty, a trusted name in the world of Egyptian herbs and spices.
                We are more than an exporting company — we are growers, processors, and quality-driven 
                partners to clients around the world. With our own privately managed farm and a commitment 
                to responsible agriculture, we ensure that every batch is cultivated, processed, and exported 
                under strict quality standards.
              </p>
              <blockquote className="about-hero-quote">
                At Rafferty, we believe that quality starts from the soil — and we proudly manage every 
                step until the product reaches your facility.
              </blockquote>
            </div>
            <div className="about-hero-image-wrapper">
              <img 
                src="./images/DSC_6539.webp" 
                alt="Rafferty farms in Egypt" 
              />
            </div>
          </div>
        </section>

        {/* --- Stats Banner --- */}
        <section className="stats-banner">
          <div className="stats-container">
            <div className="stat-item">
              <div className="stat-number">10+</div>
              <div className="stat-label">Years of Excellence</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">20+</div>
              <div className="stat-label">Premium Products</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">7+</div>
              <div className="stat-label">Countries Served</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Quality Assured</div>
            </div>
          </div>
        </section>

        {/* --- Vision & Mission Section --- */}
        <section className="section-container">
          <div className="section-header">
            <div className="section-label">Our Foundation</div>
            <h2 className="section-title">Vision & Mission</h2>
            <p className="section-subtitle">
              Driven by excellence, guided by integrity, committed to sustainable growth
            </p>
          </div>
          
          <div className="vision-mission-grid">
            <div className="vm-card">
              <div className="vm-card-header">
                <div className="vm-icon">
                  <FontAwesomeIcon icon={faLeaf} />
                </div>
                <h3>Our Vision</h3>
              </div>
              <div className="vm-card-content">
                <p>
                  To become a leading global supplier of premium Egyptian herbs and spices, setting 
                  new standards for quality, consistency, and sustainability in the international market.
                </p>
              </div>
            </div>

            <div className="vm-card">
              <div className="vm-card-header">
                <div className="vm-icon">
                  <FontAwesomeIcon icon={faAward} />
                </div>
                <h3>Our Mission</h3>
              </div>
              <div className="vm-card-content">
                <ul className="mission-list">
                  <li>To maintain full control over our supply chain from cultivation to export</li>
                  <li>To deliver bulk herbs and spices that meet international safety and quality requirements</li>
                  <li>To provide reliable and tailored solutions for B2B clients worldwide</li>
                  <li>To cultivate long-term partnerships built on trust and transparency</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* --- Our Farm Section --- */}
        <section className="section-container bg-light">
          <div className="section-header">
            <div className="section-label">Where It All Begins</div>
            <h2 className="section-title">Our Farm</h2>
            <p className="section-subtitle">
              Complete control from seed to shipment, ensuring unmatched quality and traceability
            </p>
          </div>

          <div className="farm-section">
            <div className="farm-image">
              <img 
                src="./images/DSC_6511.webp" 
                alt="Rafferty farm operations" 
              />
            </div>

            <div className="farm-content">
              <h3>
                <FontAwesomeIcon icon={faTractor} />
                Certified Excellence
              </h3>
              <p>
                Rafferty owns and manages its own farm where we implement cutting-edge agricultural 
                practices that prioritize both quality and environmental responsibility.
              </p>

              <div className="farm-features">
                <div className="farm-feature-item">
                  <p>Sustainable agricultural practices</p>
                </div>
                <div className="farm-feature-item">
                  <p>Traceability from seed to shipment</p>
                </div>
                <div className="farm-feature-item">
                  <p>Controlled cultivation methods</p>
                </div>
                <div className="farm-feature-item">
                  <p>Soil health & irrigation monitoring</p>
                </div>
                <div className="farm-feature-item">
                  <p>Zero-compromise pesticide management</p>
                </div>
                <div className="farm-feature-item">
                  <p>Advanced quality control systems</p>
                </div>
              </div>

              <div className="farm-highlight">
                <p>
                  <FontAwesomeIcon icon={faCheckCircle} style={{marginRight: '12px'}} />
                  Our farm ensures consistency of supply, stable quality, and full transparency — 
                  a major advantage for importers, blenders, and industrial buyers.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* --- Why Rafferty Section --- */}
        <section className="section-container">
          <div className="section-header">
            <div className="section-label">The Rafferty Difference</div>
            <h2 className="section-title">Why Choose Rafferty</h2>
            <p className="section-subtitle">
              We combine all the pillars that B2B importers need for successful, long-term partnerships
            </p>
          </div>

          <div className="why-rafferty-grid">
            <div className="why-item">
              <div className="why-item-header">
                <div className="why-number">1</div>
                <h4>Full Chain Control</h4>
              </div>
              <p>
                We grow, source, process, and export with complete transparency. Every step is 
                documented and verified to ensure the highest standards.
              </p>
            </div>

            <div className="why-item">
              <div className="why-item-header">
                <div className="why-number">2</div>
                <h4>Consistency & Reliability</h4>
              </div>
              <p>
                Our own farm guarantees stable quality and uninterrupted supply year-round, 
                eliminating uncertainty in your procurement.
              </p>
            </div>

            <div className="why-item">
              <div className="why-item-header">
                <div className="why-number">3</div>
                <h4>International Compliance</h4>
              </div>
              <p>
                All products undergo strict pesticide and microbiology testing to meet EU, 
                US, and international food safety requirements.
              </p>
            </div>

            <div className="why-item">
              <div className="why-item-header">
                <div className="why-number">4</div>
                <h4>Expert Team</h4>
              </div>
              <p>
                Years of professional experience in cultivation, processing, and export logistics 
                ensure smooth operations and reliable partnerships.
              </p>
            </div>

            <div className="why-item">
              <div className="why-item-header">
                <div className="why-number">5</div>
                <h4>Specification Flexibility</h4>
              </div>
              <p>
                We customize cut size, purity levels, and processing methods to match your 
                exact requirements and market needs.
              </p>
            </div>
          </div>
        </section>

        {/* --- Quality Testing Section --- */}
        <section className="section-container bg-light">
          <div className="section-header">
            <div className="section-label">Uncompromising Standards</div>
            <h2 className="section-title">Quality Assurance & Testing</h2>
            <p className="section-subtitle">
              Every batch undergoes rigorous testing to ensure safety, purity, and compliance with 
              international standards
            </p>
          </div>

          <div className="testing-grid">
            <div className="testing-card">
              <div className="testing-icon-wrapper">
                <FontAwesomeIcon icon={faShieldAlt} />
              </div>
              <h4>Pesticide Residue Analysis</h4>
              <p>
                Comprehensive testing for EU and US compliance, ensuring all residue levels are 
                within safe, legal limits for international trade.
              </p>
            </div>

            <div className="testing-card">
              <div className="testing-icon-wrapper">
                <FontAwesomeIcon icon={faMicroscope} />
              </div>
              <h4>Microbiology Testing</h4>
              <p>
                Advanced microbiological screening for pathogens, bacteria, and contaminants to 
                guarantee food safety and shelf stability.
              </p>
            </div>

            <div className="testing-card">
              <div className="testing-icon-wrapper">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <h4>Physical Purity Testing</h4>
              <p>
                Multi-stage inspection using precision sieves, optical sorting technology, and 
                manual quality checks for optimal purity.
              </p>
            </div>
          </div>

          <div className="testing-footer">
            <div className="testing-footer-content">
              <div className="testing-footer-icon">
                <FontAwesomeIcon icon={faCertificate} />
              </div>
              <p>
                We provide complete documentation and full traceability for every shipment, 
                including certificates of analysis, origin certificates, and phytosanitary documentation.
              </p>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}

export default AboutUs;